import { useState, useEffect } from "react";
import { api, errMsg, API } from "../lib/api";
import { Trash2, AlertTriangle, Download, Upload, FileSpreadsheet } from "lucide-react";
import { fmtIDR, PhoneLink } from "../lib/format";
import { useLang } from "../lib/i18n";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CrudPage } from "../components/CrudPage";
import { useAuth } from "../contexts/AuthContext";

function BusinessSettings() {
  const { t } = useLang();
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);

  if (!form) return <div className="py-10 text-center text-muted-foreground">{t("loading")}</div>;

  const save = async () => {
    try {
      await api.put("/settings", {
        ...form,
        target_food_cost: Number(form.target_food_cost) > 1 ? Number(form.target_food_cost) / 100 : Number(form.target_food_cost),
        margin_profit: Number(form.margin_profit) > 1 ? Number(form.margin_profit) / 100 : Number(form.margin_profit),
        tax_pct: Number(form.tax_pct) > 1 ? Number(form.tax_pct) / 100 : Number(form.tax_pct),
        service_pct: Number(form.service_pct) > 1 ? Number(form.service_pct) / 100 : Number(form.service_pct),
      });
      toast.success(t("save") + " OK");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pct = (v) => Math.round((v || 0) * 10000) / 100;

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-xs max-w-2xl" data-testid="business-settings-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { name: "business_name", label: t("businessName"), type: "text" },
          { name: "target_food_cost", label: t("targetFoodCost"), type: "number", isPct: true },
          { name: "margin_profit", label: t("marginProfit"), type: "number", isPct: true },
          { name: "tax_pct", label: t("taxPct"), type: "number", isPct: true },
          { name: "service_pct", label: t("servicePct"), type: "number", isPct: true },
          { name: "overhead_pct", label: t("overheadPct"), type: "number", isPct: true },
          { name: "rounding", label: t("rounding"), type: "number" },
          { name: "receipt_header", label: t("receiptHeader"), type: "text" },
          { name: "receipt_footer", label: t("receiptFooter"), type: "text" },
        ].map((f) => (
          <div key={f.name}>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
            <Input
              data-testid={`setting-${f.name}`}
              type={f.type}
              value={f.isPct ? pct(form[f.name]) : form[f.name] ?? ""}
              onChange={(e) => setForm({ ...form, [f.name]: f.isPct ? Number(e.target.value) / 100 : f.type === "number" ? Number(e.target.value) : e.target.value })}
              className="mt-1.5"
            />
          </div>
        ))}
        <div>
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">{t("costMethod")}</Label>
          <Select value={form.cost_method} onValueChange={(v) => setForm({ ...form, cost_method: v })}>
            <SelectTrigger data-testid="setting-cost-method" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="average">Average Cost</SelectItem>
              <SelectItem value="fifo">FIFO</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
        Harga Jual = HPP / Target Food Cost → HPP Rp5.000, FC 40% = <span className="font-mono font-bold">Rp12.500</span>
      </div>
      <Button data-testid="settings-save-btn" onClick={save} className="mt-5 bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
    </div>
  );
}

function UsersTab() {
  const { t } = useLang();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [outlets, setOutlets] = useState([]);
  useEffect(() => { api.get("/meta/all").then((r) => setOutlets(r.data.outlets || [])).catch(() => {}); }, []);
  const outletName = (id) => outlets.find((o) => o.id === id)?.name || "-";
  return (
    <CrudPage
      title={t("users")}
      endpoint="users"
      testPrefix="users"
      canWrite={isOwner}
      columns={[
        { key: "name", label: t("name") },
        { key: "email", label: t("email") },
        { key: "role", label: t("role"), render: (u) => <span className="bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase">{u.role}</span> },
        { key: "outlet_id", label: t("outlet"), render: (u) => u.outlet_id ? <span className="text-xs">{outletName(u.outlet_id)}</span> : <span className="text-xs text-muted-foreground">Bebas</span> },
        { key: "active", label: t("active"), render: (u) => (u.active ? "✓" : "–") },
      ]}
      fields={[
        { name: "name", label: t("name") },
        { name: "email", label: t("email") },
        { name: "role", label: t("role"), type: "select", options: [{ value: "owner", label: "Owner" }, { value: "admin", label: "Admin" }, { value: "kasir", label: "Kasir" }] },
        { name: "outlet_id", label: `${t("outlet")} (admin/kasir dibatasi ke outlet ini; kosong = bebas)`, type: "select", options: outlets.map((o) => ({ value: o.id, label: o.name })) },
        { name: "password", label: `${t("password")} (opsional)` },
        { name: "active", label: t("active"), type: "checkbox" },
      ]}
    />
  );
}

function RolePermissions() {
  const { t } = useLang();
  const [perms, setPerms] = useState(null);

  const MENUS = [
    { id: "dashboard", label: t("nav_dashboard") },
    { id: "masters", label: "Master Data (Produk/Bahan/Base)" },
    { id: "recipes", label: t("nav_recipes") },
    { id: "inventory", label: t("nav_inventory") },
    { id: "purchasing", label: t("nav_purchasing") },
    { id: "pos", label: t("nav_pos") },
    { id: "customers", label: t("nav_customers") },
    { id: "finance", label: t("nav_finance") },
    { id: "reports", label: t("nav_reports") },
    { id: "settings", label: t("nav_settings") },
  ];

  useEffect(() => {
    api.get("/settings/role-permissions").then((r) => setPerms({ admin: r.data.admin, kasir: r.data.kasir })).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!perms) return <div className="py-10 text-center text-muted-foreground">{t("loading")}</div>;

  const toggle = (role, id) => setPerms({ ...perms, [role]: perms[role].includes(id) ? perms[role].filter((x) => x !== id) : [...perms[role], id] });

  const save = async () => {
    try {
      await api.put("/settings/role-permissions", perms);
      toast.success(t("save") + " OK");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-xs max-w-2xl" data-testid="role-permissions-panel">
      <h3 className="font-heading font-semibold mb-1">{t("menuAccess")}</h3>
      <p className="text-xs text-muted-foreground mb-4">Owner selalu memiliki akses penuh. Perubahan berlaku saat user me-refresh halaman atau login ulang.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
            <th className="text-left py-2">Menu</th>
            <th className="text-center py-2 w-24">Admin</th>
            <th className="text-center py-2 w-24">Kasir</th>
          </tr>
        </thead>
        <tbody>
          {MENUS.map((m) => (
            <tr key={m.id} className="border-b border-border last:border-0" data-testid={`perm-row-${m.id}`}>
              <td className="py-2.5">{m.label}</td>
              {["admin", "kasir"].map((role) => (
                <td key={role} className="py-2.5 text-center">
                  <input
                    type="checkbox"
                    data-testid={`perm-${role}-${m.id}`}
                    checked={perms[role].includes(m.id)}
                    onChange={() => toggle(role, m.id)}
                    className="accent-amber-600 w-4 h-4 cursor-pointer"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <Button data-testid="role-permissions-save-btn" onClick={save} className="mt-4 bg-amber-600 hover:bg-amber-700 text-white">{t("save")}</Button>
    </div>
  );
}

function ResetData() {
  const { t } = useLang();
  const [busy, setBusy] = useState("");
  const [selTrx, setSelTrx] = useState([]);
  const [selMaster, setSelMaster] = useState([]);

  const TRX_ITEMS = [
    { id: "transactions", label: "Transaksi POS" },
    { id: "purchases", label: "Pembelian" },
    { id: "purchase_orders", label: "Purchase Order" },
    { id: "stock_movements", label: "Mutasi Stok (+ reset stok & stok per outlet ke 0)" },
    { id: "stock_transfers", label: "Transfer Antar Outlet" },
    { id: "fifo_layers", label: "FIFO Layers" },
    { id: "cashflows", label: "Arus Kas" },
    { id: "expenses", label: "Pengeluaran" },
    { id: "incomes", label: "Pemasukan" },
  ];
  const MASTER_ITEMS = [
    { id: "products", label: "Produk" },
    { id: "ingredients", label: "Bahan Baku" },
    { id: "recipes", label: "Resep & HPP" },
    { id: "recipe_history", label: "Riwayat Resep" },
    { id: "bases", label: "Base (Pre-Mix)" },
    { id: "customers", label: "Pelanggan" },
    { id: "suppliers", label: "Supplier" },
    { id: "categories", label: "Kategori" },
    { id: "units", label: "Satuan" },
    { id: "outlets", label: "Outlet" },
    { id: "delivery_apps", label: "Aplikasi Delivery" },
    ...TRX_ITEMS,
  ];

  const toggle = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const doReset = async (scope, selected) => {
    if (selected.length === 0) { toast.error("Pilih minimal satu item"); return; }
    if (!window.confirm(`Yakin hapus ${selected.length} item terpilih? Tindakan ini tidak dapat dibatalkan.`)) return;
    setBusy(scope);
    try {
      const r = await api.post("/settings/reset-data", { scope, collections: selected });
      const n = Object.values(r.data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Reset berhasil — ${n} data dihapus`);
      if (scope === "transactions") setSelTrx([]); else setSelMaster([]);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const panel = (title, desc, items, sel, setSel, scope, danger) => (
    <div className={`border rounded-lg p-4 ${danger ? "border-rose-200 dark:border-rose-900 bg-rose-50/50 dark:bg-rose-950/20" : "border-border"}`}>
      <div className="flex items-center justify-between mb-1">
        <div className={`font-medium text-sm ${danger ? "text-rose-600" : ""}`}>{title}</div>
        <button type="button" data-testid={`${scope}-select-all-btn`} onClick={() => setSel(sel.length === items.length ? [] : items.map((i) => i.id))} className="text-xs text-amber-600 hover:underline">
          {sel.length === items.length ? "Batal Pilih" : "Pilih Semua"}
        </button>
      </div>
      <div className="text-xs text-muted-foreground mb-3">{desc}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {items.map((it) => (
          <label key={it.id} data-testid={`${scope}-item-${it.id}`} className={`flex items-center gap-2 text-sm border rounded-lg px-3 py-2 cursor-pointer transition-colors ${sel.includes(it.id) ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30" : "border-border hover:bg-muted/50"}`}>
            <input type="checkbox" checked={sel.includes(it.id)} onChange={() => toggle(sel, setSel, it.id)} className="accent-amber-600 shrink-0" />
            <span className="truncate">{it.label}</span>
          </label>
        ))}
      </div>
      <Button data-testid={`${scope}-delete-btn`} variant={danger ? "default" : "outline"} disabled={!sel.length || !!busy} onClick={() => doReset(scope, sel)}
        className={danger ? "bg-rose-600 hover:bg-rose-700 text-white" : "border-rose-300 dark:border-rose-800 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"}>
        <Trash2 className="w-4 h-4 mr-1.5" /> {busy === scope ? "..." : `${t("delete")} Terpilih (${sel.length})`}
      </Button>
    </div>
  );

  return (
    <div className="bg-card border border-rose-200 dark:border-rose-900 rounded-xl p-6 shadow-xs max-w-3xl" data-testid="reset-data-panel">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-5 h-5 text-rose-600" />
        <h3 className="font-heading font-semibold text-rose-600">{t("resetData")}</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-5">Pilih item yang akan dihapus untuk kembali ke kondisi awal. Tindakan ini tidak dapat dibatalkan.</p>
      <div className="space-y-4">
        {panel(t("resetTransactions"), "Pilih data transaksi yang akan dihapus. Memilih Mutasi Stok juga me-reset stok bahan ke 0.", TRX_ITEMS, selTrx, setSelTrx, "transactions", false)}
        {panel(t("resetAll"), "Pilih data master dan/atau transaksi yang akan dihapus. Akun user & pengaturan bisnis tetap.", MASTER_ITEMS, selMaster, setSelMaster, "all", true)}
      </div>
    </div>
  );
}

function BackupRecovery() {
  const MODULES = [
    { id: "ingredients", label: "Bahan Baku" }, { id: "products", label: "Produk" },
    { id: "recipes", label: "Resep & HPP" }, { id: "bases", label: "Base (Pre-Mix)" },
    { id: "categories", label: "Kategori" }, { id: "units", label: "Satuan" },
    { id: "outlets", label: "Outlet" }, { id: "suppliers", label: "Supplier" },
    { id: "customers", label: "Pelanggan" }, { id: "delivery_apps", label: "Aplikasi Delivery" },
    { id: "users", label: "Pengguna" }, { id: "transactions", label: "Transaksi POS" },
    { id: "purchases", label: "Pembelian" }, { id: "expenses", label: "Pengeluaran" },
    { id: "incomes", label: "Pemasukan" },
  ];

  const upload = async (mod, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/settings/recover/${mod}`, fd);
      toast.success(`${mod}: ${r.data.updated} diupdate · ${r.data.created} dibuat · ${r.data.skipped} diabaikan`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const uploadAll = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/settings/recover-all", fd);
      const mods = Object.entries(r.data.modules || {}).map(([k, v]) => `${k}: ${v.updated} update/${v.created} baru`).join(" · ");
      toast.success(`Recovery keseluruhan selesai — ${mods}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const btnCls = "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors";

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-xs max-w-3xl" data-testid="backup-recovery-panel">
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-heading font-semibold">Backup & Recovery</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Backup data per menu ke Excel, unduh template, isi, lalu upload untuk recovery.
          Aturan: baris dicocokkan via kolom <span className="font-mono font-semibold">id</span> (atau nama/email/nomor) — sel yang <b>terisi akan diupdate</b>, sel <b>kosong diabaikan</b>. Baris tanpa id akan dibuat sebagai data baru.
        </p>
      </div>
      <div className="px-5 py-4 border-b border-border bg-amber-50/50 dark:bg-amber-950/20 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="font-semibold text-sm">Semua Data (1 file)</div>
          <div className="text-xs text-muted-foreground">Backup seluruh menu dalam satu file Excel (1 sheet per menu), dan recovery sekaligus dari file yang sama.</div>
        </div>
        <a data-testid="backup-all-btn" href={`${API}/settings/backup-all`} className={`${btnCls} border-emerald-300 dark:border-emerald-800 bg-emerald-600 text-white hover:bg-emerald-700`}>
          <Download className="w-3.5 h-3.5" /> Backup Semua
        </a>
        <a data-testid="template-all-btn" href={`${API}/settings/template-all`} className={`${btnCls} border-border text-muted-foreground hover:bg-muted`}>
          <FileSpreadsheet className="w-3.5 h-3.5" /> Template Semua
        </a>
        <label className={`${btnCls} bg-amber-600 text-white hover:bg-amber-700 cursor-pointer`} data-testid="recover-all-label">
          <Upload className="w-3.5 h-3.5" /> Recovery Semua
          <input data-testid="recover-all-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => { uploadAll(e.target.files[0]); e.target.value = ""; }} />
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-muted/50">
            {["Menu", "Backup", "Template", "Recovery"].map((h) => <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>)}
          </tr></thead>
          <tbody>
            {MODULES.map((m) => (
              <tr key={m.id} data-testid={`backup-row-${m.id}`} className="border-b border-border last:border-0">
                <td className="px-4 py-2.5 font-medium">{m.label}</td>
                <td className="px-4 py-2.5">
                  <a data-testid={`backup-${m.id}`} href={`${API}/settings/backup/${m.id}`} className={`${btnCls} border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40`}>
                    <Download className="w-3.5 h-3.5" /> Backup
                  </a>
                </td>
                <td className="px-4 py-2.5">
                  <a data-testid={`template-${m.id}`} href={`${API}/settings/template/${m.id}`} className={`${btnCls} border-border text-muted-foreground hover:bg-muted`}>
                    <FileSpreadsheet className="w-3.5 h-3.5" /> Template
                  </a>
                </td>
                <td className="px-4 py-2.5">
                  <label className={`${btnCls} border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 cursor-pointer`} data-testid={`recover-label-${m.id}`}>
                    <Upload className="w-3.5 h-3.5" /> Recovery
                    <input data-testid={`recover-${m.id}`} type="file" accept=".xlsx" className="hidden" onChange={(e) => { upload(m.id, e.target.files[0]); e.target.value = ""; }} />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Settings() {
  const { t } = useLang();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const tabs = [
    { id: "business", label: t("businessSettings"), content: <BusinessSettings /> },
    ...(isOwner ? [{ id: "users", label: t("users"), content: <UsersTab /> }, { id: "access", label: t("menuAccess"), content: <RolePermissions /> }] : []),
    { id: "outlets", label: t("outlets"), content: <CrudPage title={t("outlets")} endpoint="outlets" testPrefix="outlets" defaults={{ active: true }} columns={[{ key: "name", label: t("name") }, { key: "address", label: "Alamat" }, { key: "phone", label: "Telepon", render: (r) => <PhoneLink phone={r.phone} /> }]} fields={[{ name: "name", label: t("name") }, { name: "address", label: "Alamat", type: "textarea" }, { name: "phone", label: "Telepon" }, { name: "active", label: t("active"), type: "checkbox" }]} /> },
    { id: "categories", label: t("categories"), content: <CrudPage title={t("categories")} endpoint="categories" testPrefix="categories" columns={[{ key: "name", label: t("name") }, { key: "type", label: "Type" }, { key: "description", label: "Deskripsi" }]} fields={[{ name: "name", label: t("name") }, { name: "type", label: "Type", type: "select", options: [{ value: "product", label: "Produk" }, { value: "ingredient", label: "Bahan" }] }, { name: "description", label: "Deskripsi", type: "textarea" }]} /> },
    { id: "units", label: t("units"), content: <CrudPage title={t("units")} endpoint="units" testPrefix="units" columns={[{ key: "name", label: t("name") }, { key: "abbreviation", label: "Singkatan" }]} fields={[{ name: "name", label: t("name") }, { name: "abbreviation", label: "Singkatan" }]} /> },
    { id: "suppliers", label: t("suppliers"), content: <CrudPage title={t("suppliers")} endpoint="suppliers" testPrefix="suppliers" columns={[{ key: "name", label: t("name") }, { key: "contact", label: "Kontak" }, { key: "phone", label: "Telepon", render: (r) => <PhoneLink phone={r.phone} /> }, { key: "email", label: t("email") }]} fields={[{ name: "name", label: t("name") }, { name: "contact", label: "Kontak" }, { name: "phone", label: "Telepon" }, { name: "email", label: t("email") }, { name: "address", label: "Alamat", type: "textarea" }]} /> },
    { id: "delivery", label: t("deliveryApp"), content: <CrudPage title={t("deliveryApp")} endpoint="delivery_apps" testPrefix="delivery-apps" defaults={{ active: true }} columns={[{ key: "name", label: t("name") }, { key: "fee_pct", label: "Fee (%)" }, { key: "fee_fixed", label: "Fee Tetap", render: (d) => fmtIDR(d.fee_fixed || 0) }, { key: "active", label: t("active"), render: (d) => (d.active ? "✓" : "–") }]} fields={[{ name: "name", label: t("name") }, { name: "fee_pct", label: "Fee (%)", type: "number" }, { name: "fee_fixed", label: "Fee Tetap (Rp)", type: "number" }, { name: "active", label: t("active"), type: "checkbox" }]} /> },
    ...(isOwner ? [{ id: "data", label: t("resetData"), content: <ResetData /> }] : []),
    { id: "backup", label: "Backup & Recovery", content: <BackupRecovery /> },
  ];

  return (
    <div data-testid="settings-page">
      <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight mb-5">{t("nav_settings")}</h1>
      <Tabs defaultValue="business">
        <TabsList data-testid="settings-tabs" className="flex-wrap h-auto mb-4">
          {tabs.map((tb) => <TabsTrigger key={tb.id} value={tb.id} data-testid={`settings-tab-${tb.id}`}>{tb.label}</TabsTrigger>)}
        </TabsList>
        {tabs.map((tb) => <TabsContent key={tb.id} value={tb.id}>{tb.content}</TabsContent>)}
      </Tabs>
    </div>
  );
}
